import type { SelectChangeEvent } from '@mui/material';
import type { CurrentReview } from '@shared/domain/review';
import { Box, Chip, Menu, MenuItem, Select } from '@mui/material';
import AuthorBadge from '@shared/components/AuthorBadge';
import React, { useState } from 'react';
import { CompanyName, MobileActionsButton, MobileDropdownContainer, MobileMetadataRow } from './ReviewBanner.styles';

interface ReviewBannerMobileProps {
  companyName: string;
  currentReview?: CurrentReview;
  sections: { key: string; title: string }[];
  selectedSection: string;
  isEditMode: boolean;
  isAdmin: boolean;
  isOwnReview: boolean;
  canEditReview: boolean;
  onSectionChange: (event: SelectChangeEvent<string>) => void;
  onCompanyClick: () => void;
  onAskQuestion: () => void;
  onShareDealRetro: () => void;
  onRequestConsultation: () => void;
  onToggleEditMode: () => void;
}

export const ReviewBannerMobile: React.FC<ReviewBannerMobileProps> = ({
  companyName,
  currentReview,
  sections,
  selectedSection,
  isEditMode,
  isAdmin,
  isOwnReview,
  canEditReview,
  onSectionChange,
  onCompanyClick,
  onAskQuestion,
  onShareDealRetro,
  onRequestConsultation,
  onToggleEditMode,
}) => {
  const [mobileActionsAnchor, setMobileActionsAnchor] = useState<null | HTMLElement>(null);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <CompanyName onClick={onCompanyClick} style={{ fontSize: '18px', marginBottom: '12px' }}>
          {companyName}
        </CompanyName>
        <MobileMetadataRow sx={{ mb: 1 }}>
          <Chip label={currentReview?.dealArea || 'N/A'} size="small" />
          <Chip label={currentReview?.dealDate || 'N/A'} size="small" />
        </MobileMetadataRow>
        {currentReview?.userId && (
          <Box>
            <AuthorBadge userId={currentReview.userId} user={currentReview.user} size="small" />
          </Box>
        )}
      </Box>

      <MobileDropdownContainer sx={{ mb: 1 }}>
        <Select value={selectedSection} onChange={onSectionChange} displayEmpty fullWidth>
          <MenuItem value="" disabled>
            Jump to Section
          </MenuItem>
          {sections.map(({ key, title }) => (
            <MenuItem key={key} value={key}>
              {title}
            </MenuItem>
          ))}
          {!isEditMode && <MenuItem value="qa">Q&A</MenuItem>}
        </Select>
      </MobileDropdownContainer>

      <MobileActionsButton onClick={(e) => setMobileActionsAnchor(e.currentTarget)} variantType="success">
        Actions ▼
      </MobileActionsButton>

      <Menu
        anchorEl={mobileActionsAnchor}
        open={Boolean(mobileActionsAnchor)}
        onClose={() => setMobileActionsAnchor(null)}
        PaperProps={{
          sx: {
            mt: 1,
            width: 'calc(100% - 32px)',
            maxWidth: '400px',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        {!isOwnReview && (
          <>
            <MenuItem
              onClick={() => {
                setMobileActionsAnchor(null);
                onAskQuestion();
              }}
              sx={{ py: 1.5, fontSize: '0.9375rem' }}
            >
              💬 Ask a Question
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMobileActionsAnchor(null);
                onRequestConsultation();
              }}
              sx={{ py: 1.5, fontSize: '0.9375rem' }}
            >
              🤝 Request Consultation
            </MenuItem>
          </>
        )}
        <MenuItem
          onClick={() => {
            setMobileActionsAnchor(null);
            onShareDealRetro();
          }}
          sx={{ py: 1.5, fontSize: '0.9375rem' }}
        >
          📧 Share Deal Retro
        </MenuItem>
        {canEditReview && (
          <MenuItem
            onClick={() => {
              setMobileActionsAnchor(null);
              onToggleEditMode();
            }}
            sx={{ py: 1.5, fontSize: '0.9375rem' }}
          >
            ✏️ {isEditMode ? 'View' : 'Edit'}
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
